import React from 'react'

import { TidewaysStorage } from '../app/TidewaysStorage'
import Router from '../router'
import { TimeLineEvent, TimelineSpan } from '../domain/TimelineSpan'
import { BottleneckIdentifications, Trace, TraceSpanCategory, TraceTick } from '../domain/Trace'
import { LayerReport } from '../domain/TraceSummary'
import Chart from './timeline/chart'
import Span from './timeline/span'
import Event from './timeline/event'
import { TimelineLayoutModeDropdown } from './timeline/TimelineLayoutModeDropdown'
import { SummaryLineDetails } from './summary-line-details'
import { PaginatedSpanDetails } from './tracedetails/PaginatedSpanDetails'
import { SpanTraceInfo } from './tracedetails/TraceDetailsSpan'
import { groupSpans } from './groupSpans'
import { SummaryLine } from './summary'
import { Box } from '../ui/box'

import SpanService from './spanService'
import TimelineTraceDataTransformer, { TimelineLayoutMode } from './timeline/TimelineTraceDataTransformer'
import { memoryConverter } from '../helper/memoryConverter'
import { TraceDetailsLayerReport } from './tracedetails/TraceDetailsLayerReport'
import { formatMilliseconds } from '../helper/milliseconds'

const DEFAULT_ROUTE = 'XhprofProfilerBundle.Profile.quick'

type TimelineCategories = { [category: string]: TraceSpanCategory }

interface TimelineRouteParams {
    span?: string
    call?: string
    e?: string | number
    displayMemory?: string | number | boolean
    [key: string]: string | number | boolean | undefined
}

interface Props {
    categories: TimelineCategories
    traces: Trace[]
    hasCallgraph?: boolean
    hasMemory?: boolean
    router: Router
    route?: string
    params?: TimelineRouteParams
    publicVersion?: boolean
    operationId?: number
    layerReport?: LayerReport
    maxSpanLimit: number
    hasWaterfallFeatureFlag?: boolean
    bottleneckIdentifications?: BottleneckIdentifications
}

interface State {
    toggleMemory: boolean
    displayChildTraces: boolean
    chartToTop: number
    layoutMode: TimelineLayoutMode
}

interface TimelineTraceBounds {
    traceId: string
    service: string
    environment: string
    measureDate: Trace['measureDate']
    serverName: string
    xStart: number
    xEnd: number
    yStart: string
    yEnd: string
    type: 'trace'
}

type TimelineGlobalEventWithOffset = Trace['globalEvents'][number] & {
    yStart: string
    startY: string
}

interface TimelinePlotValues {
    s: number
    t: number
}

interface TimelineChart {
    create(
        rootElement: SVGSVGElement,
        width: number,
        plotValuesCallback: (span: TimelineSpan, event?: TimeLineEvent) => TimelinePlotValues,
        tickFormatCallback: (value: number) => string,
    ): void
    update(
        spans: TimelineSpan[],
        ticks: TraceTick[],
        globalEvents: TimelineGlobalEventWithOffset[],
        traceBounds: TimelineTraceBounds[],
        categories: TimelineCategories,
        selectedSpanId: string | null,
        selectedEvent: string | number | null,
        refreshScales: boolean,
    ): void
    destroy(): void
}

// Cast the legacy JS chart module to a constructor returning the typed chart instance.
const TimelineChartConstructor = (Chart as unknown) as new () => TimelineChart

const getTraceServerName = (trace: Trace): string => {
    return trace.server ? trace.server.name : 'unknown server'
}

const isDefinedTickId = (tickId: string | undefined): tickId is string => Boolean(tickId)

class Timeline extends React.Component<Props, State> {
    public static defaultProps: Partial<Props> = {
        route: DEFAULT_ROUTE,
    }

    private chart: TimelineChart
    private spanService: SpanService
    private traceService: TimelineTraceDataTransformer
    private traces: Trace[] = []
    private spans: TimelineSpan[] = []
    private spansHidden = 0
    private spansHiddenThreshold = 0
    private svg: SVGSVGElement | null = null

    public constructor(props: Props) {
        super(props)
        this.chart = new TimelineChartConstructor()
        this.spanService = new SpanService()
        this.traceService = new TimelineTraceDataTransformer()
        this.state = {
            toggleMemory: this.props.params?.displayMemory == 1,
            displayChildTraces: true,
            chartToTop: 170, // 170px is the default position of the chart to the top of the page
            layoutMode: TidewaysStorage.getTimelineLayoutMode(),
        }
    }

    private plotValuesCallback = (span: TimelineSpan, event?: TimeLineEvent): TimelinePlotValues => {
        if (this.state.toggleMemory) {
            return event ? { s: span.ms || 0, t: 0 } : { s: span.ms || 0, t: span.mc || 0 }
        }

        return event ? { s: event.cs, t: event.wt } : { s: span.cs, t: span.wt }
    }

    private tickFormatCallback = (value: number): string => {
        if (this.state.toggleMemory) {
            return memoryConverter(value, 1000, 4)
        }

        return formatMilliseconds(value / 1000, false, 4) || ''
    }

    private onMemoryClick = () => {
        this.setState({ toggleMemory: true })
    }

    private onTimeClick = () => {
        this.setState({ toggleMemory: false })
    }

    public componentDidMount() {
        const chartElement = this.getChartElement()

        if (!chartElement) {
            return
        }

        const parentNode = jQuery(chartElement).parent()

        this.chart.create(chartElement, parentNode.width() || 0, this.plotValuesCallback, this.tickFormatCallback)
        this.setState({ chartToTop: parentNode.offset()?.top ?? 0 })

        jQuery(chartElement).on('spanSelected', (_event: JQueryEventObject, span: TimelineSpan) => {
            this.selectSpan(span.id)
        })

        jQuery(chartElement).on('eventSelected', (_event: JQueryEventObject, span: TimelineSpan, spanEvent: TimeLineEvent) => {
            this.selectEvent(span.id, spanEvent.cs)
        })

        this.updateChart()
    }

    public componentDidUpdate(prevProps: Props, prevState: State) {
        const traceChanged = prevProps.traces !== this.props.traces
        const displayModeChanged = prevState.toggleMemory !== this.state.toggleMemory
        const childTraceModeChanged = prevState.displayChildTraces !== this.state.displayChildTraces
        const layoutModeChanged = prevState.layoutMode !== this.state.layoutMode

        this.updateChart(traceChanged || displayModeChanged || childTraceModeChanged || layoutModeChanged)
    }

    private prepareTraceData() {
        if (this.props.traces.length === 0) {
            this.traces = []
            this.spans = []
            return
        }

        const includeChildTraces = this.state.displayChildTraces && !this.state.toggleMemory
        const rootTrace = this.props.traces[0]
        const childTraces = includeChildTraces ? this.props.traces.slice(1) : []
        let traces = [rootTrace, ...childTraces]

        // Clone mutable trace data before reordering and reassigning positions.
        traces = this.traceService.cloneTraceSpanAndEventData(traces)
        traces = this.traceService.alignChildTracesToParentSpans(traces)
        this.traceService.sortTracesByStartTime(traces)

        const filterInfo = this.traceService.filterFastSpansFromTraces(traces)

        this.spansHidden = filterInfo.hidden
        this.spansHiddenThreshold = filterInfo.hiddenThreshold

        for (const trace of traces) {
            trace.spans = this.spanService.sort(trace.spans)
        }

        this.traces = traces
        this.spans = traces.flatMap(trace => trace.spans)
    }

    public componentWillUnmount() {
        jQuery(window).off('scroll')
        this.chart.destroy()
    }

    private getChartElement = (): SVGSVGElement | null => {
        return this.svg
    }

    private getSelectedSpan = (): string | null => {
        if (this.props.params?.span) {
            return this.props.params.span
        }

        if (typeof this.props.params?.call === 'string') {
            const matchingSpan = this.spans.filter(span => {
                return span.annotations && 'fn' in span.annotations && span.annotations.fn === this.props.params?.call
            })[0]

            if (matchingSpan) {
                return matchingSpan.id
            }
        }

        return this.spans[0]?.id || null
    }

    private getSelectedEvent = (): string | number | null => {
        const selectedEvent = this.props.params?.e

        return typeof selectedEvent === 'string' || typeof selectedEvent === 'number' ? selectedEvent : null
    }

    private getTraceBounds(trace: Trace): TimelineTraceBounds {
        const tickIds = [...new Set(trace.spans.map(span => span.tick).filter(isDefinedTickId))]
        const rootSpan = trace.spans[0]
        const firstTickId = tickIds[0] || rootSpan.tick || rootSpan.id
        const lastTickId = tickIds[tickIds.length - 1] || firstTickId

        return {
            traceId: trace.id,
            service: trace.service,
            environment: trace.environment,
            measureDate: trace.measureDate,
            serverName: getTraceServerName(trace),
            xStart: rootSpan.cs,
            xEnd: rootSpan.cs + rootSpan.wt,
            yStart: firstTickId,
            yEnd: lastTickId,
            type: 'trace',
        }
    }

    private getTraceGlobalEventsWithOffset(trace: Trace): TimelineGlobalEventWithOffset[] {
        const tickIds = [...new Set(trace.spans.map(span => span.tick).filter(isDefinedTickId))]
        const firstTickId = tickIds[0] || trace.spans[0]?.tick || trace.spans[0]?.id || ''

        return trace.globalEvents.map(globalEvent => ({
            yStart: firstTickId,
            startY: firstTickId,
            ...globalEvent,
        }))
    }

    private updateChart(refreshScales: boolean = false) {
        if (this.props.traces.length === 0) {
            return
        }

        const allTicks: Trace['ticks'] = {}

        for (const trace of this.props.traces) {
            Object.assign(allTicks, trace.ticks)
        }

        let ticks: TraceTick[] = this.spanService.filterTicks(allTicks, this.spans)
        let traces = this.traces
        let spans = this.spans

        const waterfallLayout = this.traceService.createWaterfallLayout(this.traces, ticks, this.state.layoutMode)

        traces = waterfallLayout.traces
        spans = waterfallLayout.spans
        ticks = waterfallLayout.ticks

        const globalEvents = this.state.toggleMemory ? [] : traces.flatMap(trace => this.getTraceGlobalEventsWithOffset(trace))

        const traceBounds = traces.slice(1).map(trace => this.getTraceBounds(trace))

        this.chart.update(
            spans,
            ticks,
            globalEvents,
            traceBounds,
            this.props.categories,
            this.getSelectedSpan(),
            this.getSelectedEvent(),
            refreshScales,
        )
    }

    private selectSpan = (spanId: string) => {
        this.setQueryParameter({
            span: spanId,
            e: undefined,
            displayMemory: this.state.toggleMemory,
        })
    }

    private selectEvent = (spanId: string, eventCs: number) => {
        this.setQueryParameter({
            span: spanId,
            e: eventCs,
            displayMemory: this.state.toggleMemory,
        })
    }

    private setQueryParameter = (values: Partial<TimelineRouteParams>) => {
        const params = { ...(this.props.params || {}), ...values }

        this.props.router.goTo(this.props.route || DEFAULT_ROUTE, params, true)
    }

    private onLayoutModeChange = (layoutMode: TimelineLayoutMode) => {
        TidewaysStorage.setTimelineLayoutMode(layoutMode)
        this.setState({ layoutMode })
    }

    private getSpanCategory = (span: TimelineSpan): TraceSpanCategory => {
        if (span.cat in this.props.categories) {
            return this.props.categories[span.cat]
        }

        const components = span.cat.split(/\./g)
        if (components[0] in this.props.categories) {
            return this.props.categories[components[0]]
        }

        let label = span.cat
        if (span.cat === 'custom' && span.custom_type) {
            label = span.custom_type
        }

        return { className: 'php', label: `Custom [${label}]` }
    }

    private groupSpansByName = (spans: TimelineSpan[]): Map<string, TimelineSpan[]> =>
        spans.reduce((groupedSpans, span) => {
            const summary = span.summary || span.name

            groupedSpans.set(summary, [...(groupedSpans.get(summary) || []), span])

            return groupedSpans
        }, new Map<string, TimelineSpan[]>())

    private findSelectedLine = (
        lines: SummaryLine[],
        spansByName: Map<string, TimelineSpan[]>,
        selectedSpanId: string | null,
    ): SummaryLine | null => {
        if (!selectedSpanId) {
            return null
        }

        const directlySelectedLine = lines.find(line => line.id === selectedSpanId)
        if (directlySelectedLine) {
            return directlySelectedLine
        }

        return (
            lines.find(line => {
                const lineSpans = spansByName.get(line.name) || []

                return lineSpans.some(span => span.id === selectedSpanId)
            }) || null
        )
    }

    private getTraceInfo = (trace: Trace): SpanTraceInfo => ({
        traceId: trace.id,
        serverName: getTraceServerName(trace),
        service: trace.service,
        isChildTrace: trace.id !== this.traces[0].id,
        environment: trace.environment,
        measureDate: trace.measureDate,
    })

    public render() {
        this.prepareTraceData()

        const rootTrace = this.traces[0]
        if (!rootTrace || this.spans.length === 0) {
            return null
        }

        const categories: TimelineCategories = {}
        const visibleCategories = new Set(this.spans.map(span => span.cat))

        for (const [key, category] of Object.entries(this.props.categories)) {
            if (visibleCategories.has(key)) {
                categories[key] = category
            }
        }

        const traceBySpanId = new Map<string, Trace>()
        for (const trace of this.traces) {
            for (const span of trace.spans) {
                traceBySpanId.set(span.id, trace)
            }
        }

        const selectedSpanId = this.getSelectedSpan()
        const selectedSpan = this.spans.find(span => span.id === selectedSpanId) || null
        const selectedSpanIsRoot = selectedSpan ? selectedSpan.id === rootTrace.spans[0]?.id : false
        const selectedTrace = selectedSpanId ? traceBySpanId.get(selectedSpanId) || null : null
        const timelineSummarySpans = groupSpans(
            this.spans.map(span => ({
                ...span,
                name: span.summary || span.name,
            })),
            this.spans[0]?.wt || 0,
        )
        const groupedSummaryLines: SummaryLine[] = timelineSummarySpans.map(span => ({
            name: span.name,
            callCount: Number(span.ct),
            wallTime: span.wt,
            wallTimePercentage: span.perc_wt != null ? span.perc_wt : 0,
            exclusiveWallTime: span.excl_wt,
            exclusiveWallTimePercentage: span.perc_excl_wt != null ? span.perc_excl_wt : 0,
            category: this.getSpanCategory(span),
            id: span.id,
            hasDetails: span.type === 'span',
            fn: span.fn,
        }))
        const spansByName = this.groupSpansByName(this.spans)
        const selectedSummaryLine = this.findSelectedLine(groupedSummaryLines, spansByName, selectedSpanId)
        const relatedSpans = selectedSummaryLine && !this.props.params?.e ? spansByName.get(selectedSummaryLine.name) || [] : []
        const traceInfoBySpanId: { [spanId: string]: SpanTraceInfo } = {}

        for (const span of relatedSpans) {
            const trace = traceBySpanId.get(span.id)

            if (trace) {
                traceInfoBySpanId[span.id] = this.getTraceInfo(trace)
            }
        }

        const showGroupedSpanDetails = Boolean(selectedSpan && selectedSummaryLine && relatedSpans.length > 1)
        const truncateSpan = this.spans.find(span => span.annotations && 'trunc_spans' in span.annotations) || null
        const route = this.props.route || DEFAULT_ROUTE
        const publicVersion = this.props.publicVersion ?? false
        const totalWallTime = this.spans[0]?.wt || 0
        const totalMemory = this.spans[0]?.mc || 0
        const selectedEvent = this.props.params?.e ? selectedSpan?.e?.find(event => event.cs === Number(this.props.params?.e)) : undefined

        return (
            <div className="row">
                <div className="col-lg-8 col-md-12">
                    <div className="timeline-chart box">
                        <div className="box-body">
                            {truncateSpan ? (
                                <div className="row">
                                    <div className="alert alert-warning">
                                        <p>
                                            This trace is very large and Tideways has truncated {truncateSpan.annotations?.trunc_spans}{' '}
                                            spans that happened after the threshold of {this.props.maxSpanLimit} spans per trace.
                                        </p>
                                    </div>
                                </div>
                            ) : null}
                            {rootTrace.memoryLimit ? (
                                <div className="row">
                                    <div className="alert alert-warning">
                                        <p>
                                            This trace contains a large amount of individual spans and Tideways was not able to process and
                                            display all of them.
                                        </p>
                                    </div>
                                </div>
                            ) : null}
                            {this.spansHidden ? (
                                <div className="row">
                                    <p>
                                        Hidden {this.spansHidden} spans slower than {(this.spansHiddenThreshold / 1000).toFixed(0)} ms. See
                                        all spans in Summary.
                                    </p>
                                </div>
                            ) : null}
                            <div className="row">
                                <div className="col-lg-5">
                                    {this.props.hasMemory ? (
                                        <div style={{ display: 'inline-block' }}>
                                            <div className="btn-group tw-mr-8">
                                                <button
                                                    type="button"
                                                    className={
                                                        this.state.toggleMemory ? 'btn btn-xs btn-default' : 'btn btn-xs btn-default active'
                                                    }
                                                    onClick={this.onTimeClick}
                                                >
                                                    Time
                                                </button>
                                                <button
                                                    type="button"
                                                    className={
                                                        this.state.toggleMemory ? 'btn btn-xs btn-default active' : 'btn btn-xs btn-default'
                                                    }
                                                    onClick={this.onMemoryClick}
                                                >
                                                    Memory
                                                </button>
                                            </div>
                                        </div>
                                    ) : null}
                                    <TimelineLayoutModeDropdown layoutMode={this.state.layoutMode} onChange={this.onLayoutModeChange} />
                                    {this.traces.length > 1 && !this.state.toggleMemory ? (
                                        <div className="checkbox checkbox-primary tw-ml-8" style={{ display: 'inline-block' }}>
                                            <input
                                                type="checkbox"
                                                name="displayChildTraces"
                                                id="timeline_displayChildTraces"
                                                style={{ top: '20px' }}
                                                checked={this.state.displayChildTraces}
                                                onChange={event => this.setState({ displayChildTraces: event.currentTarget.checked })}
                                            />
                                            <label htmlFor="timeline_displayChildTraces">Display child traces</label>
                                        </div>
                                    ) : null}
                                </div>
                                <div className="col-lg-7 legend">
                                    <ul className="categories-list list-unstyled list-inline">
                                        {Object.entries(categories).map(([key, category]) => (
                                            <li key={key}>
                                                <i className={'fa fa-circle category-' + category.className} /> {category.label}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                            <div
                                className="timeline-chart__container"
                                style={{
                                    maxHeight: `calc(100vh - ${Math.max(0, Math.round(this.state.chartToTop))}px)`,
                                }}
                            >
                                <svg
                                    ref={svg => {
                                        this.svg = svg
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4 col-md-12">
                    <div className="timeline-info">
                        {showGroupedSpanDetails ? (
                            <>
                                <Box>
                                    <SummaryLineDetails
                                        line={selectedSummaryLine!}
                                        showExclusiveWallTime={false}
                                        showTitle={false}
                                        sectionBelowTitle={
                                            relatedSpans.some(span => span.id === rootTrace.spans[0].id) && this.props.layerReport ? (
                                                <TraceDetailsLayerReport layerReport={this.props.layerReport} wt={totalWallTime} />
                                            ) : null
                                        }
                                    />
                                </Box>
                                <PaginatedSpanDetails
                                    key={selectedSummaryLine!.id}
                                    spans={relatedSpans}
                                    allSpans={this.spans}
                                    spanService={this.spanService}
                                    wt={totalWallTime}
                                    mc={totalMemory}
                                    categories={this.props.categories}
                                    route={route}
                                    publicVersion={publicVersion}
                                    selectedSpanId={selectedSpanId || undefined}
                                    traceInfoBySpanId={traceInfoBySpanId}
                                    onSelectedSpanChange={span => this.selectSpan(span.id)}
                                />
                            </>
                        ) : selectedSpan ? (
                            <Span
                                wt={totalWallTime}
                                mc={totalMemory}
                                span={selectedSpan}
                                traceInfo={selectedTrace ? this.getTraceInfo(selectedTrace) : undefined}
                                parents={this.spanService.getParents(this.spans, selectedSpan)}
                                categories={this.props.categories}
                                route={route}
                                showSettingsLink={!publicVersion}
                                sectionBelowTitle={
                                    selectedSpanIsRoot && this.props.layerReport ? (
                                        <TraceDetailsLayerReport layerReport={this.props.layerReport} wt={totalWallTime} />
                                    ) : (
                                        undefined
                                    )
                                }
                                bottleneckRoute={
                                    publicVersion
                                        ? 'XhprofCoreBundle.Public.sharedTrace.bottlenecks'
                                        : 'XhprofProfilerBundle.Profile.bottlenecksReport'
                                }
                                operationId={this.props.operationId}
                                bottleneckIdentifications={this.props.bottleneckIdentifications}
                                isRoot={selectedSpanIsRoot}
                            >
                                {selectedEvent ? (
                                    <>
                                        <hr className="tw-m-0" />
                                        <Event event={selectedEvent} categories={this.props.categories} />
                                    </>
                                ) : null}
                            </Span>
                        ) : null}
                    </div>
                </div>
            </div>
        )
    }
}

export default Timeline
